import { useEffect, useSyncExternalStore } from 'react'
import { useSelector } from 'react-redux'
import * as Sentry from '@sentry/react'
import { useQuery } from '@tanstack/react-query'

import { AccessResponse, FlagPayload } from '@/api/generated/api'
import { getAccess } from '@/api/user'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { selectCurrentEnterprise, selectCurrentUserId } from '@/store/features/user/userSlice'

export type AccessKey = 'access.sso' | 'access.team-insights' | 'access.public-api-key-management'

export type AccessKeyState =
  | { status: 'ready'; enabled: boolean }
  | { status: 'pending' }
  // The legacy rule decides. Impersonation is expected; the other causes are counted.
  | { status: 'fallback'; cause: 'impersonating' | 'key_absent' | 'fetch_error' }

// /access answers per user, and the QueryClient is a module singleton that neither sign-out nor a tenant switch clears.
// `inputs` holds what the served keys read (role, product), so a change to either starts a fresh entry.
export const accessQueryKey = (userId: string, enterpriseId: string, inputs: string, key: AccessKey) =>
  ['access', userId, enterpriseId, inputs, key] as const

export const useAccessKey = (key: AccessKey, enabled = true): AccessKeyState => {
  const userId = useSelector(selectCurrentUserId) ?? ''
  const enterprise = useSelector(selectCurrentEnterprise)
  const enterpriseId = enterprise?.id ?? ''
  // /access answers for the signed-in user, never the impersonated tenant, and is not on the
  // impersonation allowlist, so impersonated views keep the legacy rule.
  const impersonating = useSyncExternalStore(subscribeImpersonation, getImpersonation) !== null
  const inputs = `${enterprise?.userRole ?? ''}|${enterprise?.subscription?.productName ?? ''}`

  const query = useQuery({
    queryKey: accessQueryKey(userId, enterpriseId, inputs, key),
    queryFn: async ({ signal }) => (await getAccess(key, signal)).data,
    enabled: enabled && Boolean(userId) && Boolean(enterpriseId) && !impersonating,
    staleTime: 5 * 60 * 1000,
    // The legacy rule is the fallback, so a retry only lengthens how long gated sections wait.
    retry: false,
  })

  const state = resolveAccessKeyState(key, impersonating, query.data, query.isError)

  const fallbackCause = state.status === 'fallback' ? state.cause : null
  useEffect(() => {
    if (enabled && (fallbackCause === 'key_absent' || fallbackCause === 'fetch_error')) {
      Sentry.metrics.count('access.portal_fallback', 1, { attributes: { key, cause: fallbackCause } })
    }
  }, [enabled, fallbackCause, key])

  return state
}

export const resolveAccessKeyState = (
  key: AccessKey,
  impersonating: boolean,
  data: { flags: Partial<AccessResponse['flags']> } | undefined,
  isError: boolean
): AccessKeyState => {
  if (impersonating) return { status: 'fallback', cause: 'impersonating' }
  // A failed refetch keeps the last good snapshot, so data wins over the error flag.
  if (data) {
    const flag: FlagPayload | undefined = data.flags[key]
    return flag ? { status: 'ready', enabled: flag.enabled } : { status: 'fallback', cause: 'key_absent' }
  }
  if (isError) return { status: 'fallback', cause: 'fetch_error' }
  return { status: 'pending' }
}
